import React, { useState } from "react";
import { teamMembers, companyInfo } from "../data/mockData";
import { Mail, Phone, MapPin, X, User } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useInView } from "react-intersection-observer";

const TeamSection = () => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [selectedImage, setSelectedImage] = useState(null);

  return (
    <section id="contact" className="py-28 bg-white">
      <div className="max-w-[1200px] mx-auto px-6">
        {/* Header */}
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7 }}
          className="text-center mb-16"
        >
          <p className="text-sm font-medium text-[#4a7c59] tracking-widest uppercase mb-3">
            Kontakt
          </p>
          <h2 className="text-4xl md:text-5xl font-bold text-[#1d1d1f] tracking-tight mb-5">
            Wir sind für Sie da.
          </h2>
          <p className="text-lg text-[#6e6e73] max-w-xl mx-auto">
            Unser Team steht Ihnen jederzeit zur Verfügung.
          </p>
        </motion.div>

        {/* Contact Info Card */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7, delay: 0.2 }}
          className="bg-[#f5f5f7] rounded-3xl p-8 md:p-10 mb-12 flex flex-wrap items-center justify-center gap-10"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#4a7c59]/10 flex items-center justify-center">
              <Phone size={18} className="text-[#4a7c59]" />
            </div>
            <div>
              <p className="text-xs text-[#6e6e73]">Telefon</p>
              <p className="text-sm font-semibold text-[#1d1d1f]">
                {companyInfo.phone}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#4a7c59]/10 flex items-center justify-center">
              <Mail size={18} className="text-[#4a7c59]" />
            </div>
            <div>
              <p className="text-xs text-[#6e6e73]">Email</p>
              <p className="text-sm font-semibold text-[#1d1d1f]">
                {companyInfo.email}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#4a7c59]/10 flex items-center justify-center">
              <MapPin size={18} className="text-[#4a7c59]" />
            </div>
            <div>
              <p className="text-xs text-[#6e6e73]">Adresse</p>
              <p className="text-sm font-semibold text-[#1d1d1f]">
                {companyInfo.address}
              </p>
            </div>
          </div>
        </motion.div>

        {/* Team Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {teamMembers.map((member, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 40 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.3 + index * 0.1 }}
              className="group text-center p-6 rounded-3xl bg-[#f5f5f7] hover:bg-white border border-transparent hover:border-[#e5e5e5] hover:shadow-xl hover:shadow-black/5 transition-all duration-500"
            >
              {member.image ? (
                <button
                  onClick={() => setSelectedImage({ url: member.image, name: member.name })}
                  className="w-24 h-24 mx-auto mb-5 rounded-full overflow-hidden ring-4 ring-white shadow-lg cursor-pointer hover:ring-[#4a7c59] transition-all"
                >
                  <img
                    src={member.image}
                    alt={member.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                </button>
              ) : (
                <div
                  aria-label={member.name}
                  className="w-24 h-24 mx-auto mb-5 rounded-full overflow-hidden ring-4 ring-white shadow-lg bg-[#e5e5ea] flex items-center justify-center"
                >
                  <User size={40} className="text-[#9a9aa0]" />
                </div>
              )}
              <h3 className="text-base font-semibold text-[#1d1d1f] mb-0.5">
                {member.name}
              </h3>
              <p className="text-xs text-[#4a7c59] font-medium mb-3">
                {member.role}
              </p>
              <p className="text-xs text-[#6e6e73] mb-1">{member.phone}</p>
              <a
                href={`mailto:${member.email}`}
                className="text-xs text-[#4a7c59] hover:underline break-all"
              >
                Email schreiben
              </a>
              {member.whatsapp && (
                <a
                  href={`https://wa.me/${member.whatsapp}?text=${encodeURIComponent(
                    `Hallo ${member.name}, ich habe eine Frage zur Geräte-Vermietung.`
                  )}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-2 flex items-center justify-center gap-1.5 text-xs font-medium text-[#25D366] hover:underline"
                >
                  <svg viewBox="0 0 24 24" width="13" height="13" fill="currentColor" aria-hidden="true">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.52.149-.174.198-.298.297-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.71.306 1.263.489 1.694.625.712.227 1.36.195 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
                  </svg>
                  WhatsApp
                </a>
              )}
            </motion.div>
          ))}
        </div>
      </div>

      {/* Image Lightbox Modal */}
      <AnimatePresence>
        {selectedImage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSelectedImage(null)}
            className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              transition={{ duration: 0.3 }}
              onClick={(e) => e.stopPropagation()}
              className="relative max-w-3xl w-full"
            >
              <button
                onClick={() => setSelectedImage(null)}
                className="absolute -top-12 right-0 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors"
              >
                <X size={20} />
              </button>
              <img
                src={selectedImage.url}
                alt={selectedImage.name}
                className="w-full h-auto rounded-2xl shadow-2xl"
              />
              <p className="text-center text-white text-lg font-semibold mt-4">
                {selectedImage.name}
              </p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default TeamSection;
